import { computed, inject, Injectable, signal } from '@angular/core';
import { AccessibilityService } from '@core/data-access/services/accessibility/accessibility.service';
import {
  selectAreDefaultSettingsChanged,
  selectFontSizePercentage,
  selectIsHighContrastEnabled
} from '@core/data-access/store/accessibility/accessibility.selectors';
import { Store } from '@ngrx/store';
import { AccessibilitySidebarState, AccessibilitySidebarViewModel } from '../models/accessibility-sidebar.model';

@Injectable()
export class AccessibilitySidebarFacade {
  readonly #store = inject(Store);
  readonly #accessibilityService = inject(AccessibilityService);

  readonly isOpen = signal(false);

  readonly #state = this.setState();

  readonly state = computed(this.#state);

  private setState() {
    return computed<AccessibilitySidebarState>(() => ({
      viewModel: this.setViewModel()
    }));
  }

  private setViewModel(): AccessibilitySidebarViewModel {
    const isHighContrastEnabled = this.#store.selectSignal(selectIsHighContrastEnabled);
    const fontSizePercentage = this.#store.selectSignal(selectFontSizePercentage);
    const areDefaultSettingsChanged = this.#store.selectSignal(selectAreDefaultSettingsChanged);

    return {
      isOpen: this.isOpen(),
      isHighContrastEnabled: isHighContrastEnabled(),
      fontSizePercentage: fontSizePercentage(),
      areDefaultSettingsChanged: areDefaultSettingsChanged()
    };
  }

  toggleIsOpen() {
    this.isOpen.update((isOpen) => !isOpen);
  }

  toggleHighContrast() {
    this.#accessibilityService.toggleHighContrast();
  }

  increaseFontSize(currentFontSizePercentage: number) {
    this.#accessibilityService.increaseFontSize(currentFontSizePercentage);
  }

  decreaseFontSize(currentFontSizePercentage: number) {
    this.#accessibilityService.decreaseFontSize(currentFontSizePercentage);
  }

  resetState() {
    this.#accessibilityService.resetState();
  }
}
